import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  ArrowLeft, Calendar, User, CheckCircle2, Clock, Circle,
  Trash2, Plus, Paperclip, MessageSquare, Send, X, 
  Layout, DollarSign, Upload, FileText,
  Image as ImageIcon, Link as LinkIcon, AlertCircle,
  Search, History, Flag, MoreHorizontal, ChevronRight,
  ListChecks, Activity, Target, TrendingDown
} from 'lucide-react';
// @ts-ignore
import { Action } from '../types';
import { useProjectStore } from '../store/useProjectStore';
import { ActionImpact } from '../types/postit';
import { useAuth } from '../context/AuthContext';

interface ActionDetailViewProps {
  action: Action;
  onBack: () => void;
  onUpdateAction: (id: string, data: Partial<Action>) => void;
  zoom?: number;
}

// 🗓️ Função para formatar data no formato dd/mm/aaaa
const formatDateBR = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// --- COMPONENTES AUXILIARES ---

const UserAssignmentPopover: React.FC<{ currentValue?: string; onSelect: (name: string) => void; onClose: () => void }> = ({ currentValue, onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { actions, organization } = useProjectStore(); 
  
  const availableUsers = useMemo(() => {
    const users = new Set<string>();
    users.add("Eu");
    // Adicionar membros cadastrados no OrgManager (apenas visíveis)
    organization.roles.filter(r => r.visible !== false).forEach(role => users.add(role.name));
    // Adicionar responsáveis existentes nas ações
    actions.forEach(a => { if (a.responsavel && a.responsavel !== '-') users.add(a.responsavel) });
    return Array.from(users).sort();
  }, [actions, organization]);

  const filteredUsers = availableUsers.filter(u => u.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50); }, []);

  return (
    <div className="absolute left-0 top-8 z-50 w-60 border border-[var(--border-default)] bg-[var(--bg-surface)] p-1">
      <div className="mb-1 border-b border-[var(--border-default)] p-2">
        <input ref={inputRef} type="text" className="w-full border border-[var(--border-subtle)] bg-[var(--bg-canvas)] px-2 py-1.5 text-xs text-[var(--text-primary)] focus:border-[var(--nav-active-bar)] focus:outline-none" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="max-h-40 overflow-y-auto">
        {filteredUsers.map(user => (
          <button key={user} onClick={() => { onSelect(user); onClose(); }} className={`mb-0.5 flex w-full items-center justify-between px-3 py-1.5 text-left text-xs ${currentValue === user ? 'bg-[var(--bg-surface-2)] font-medium text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-2)]'}`}>
            <div className="flex items-center gap-2">
              <div className="flex h-5 w-5 items-center justify-center bg-[var(--bg-surface-3)] text-[10px] font-bold text-[var(--text-primary)]">{user.charAt(0).toUpperCase()}</div>
              {user}
            </div>
            {currentValue === user && <CheckCircle2 size={12} />}
          </button>
        ))}
        {search && !filteredUsers.includes(search) && (
          <button onClick={() => { onSelect(search); onClose(); }} className="flex w-full items-center gap-2 border-t border-[var(--border-default)] px-3 py-1.5 text-left text-xs font-medium text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)]"><Plus size={12} /> Criar: "{search}"</button>
        )}
      </div>
    </div>
  );
};

// --- TELA PRINCIPAL (Slim & Compact) ---

const ActionDetailView: React.FC<ActionDetailViewProps> = ({ action, onBack, onUpdateAction, zoom = 1 }) => {
  const { user } = useAuth();
  const canonicalAssignedId = (action as any).responsavel_id ?? (action as any).responsibleUserId ?? (action as any).responsible_user_id ?? (action as any).assigned_to ?? (action as any).user_id ?? null;
  const isOwner = Boolean(canonicalAssignedId && user && user.id === canonicalAssignedId);
  const isAdmin = Boolean(user && (user.role === 'ADMIN' || user.role === 'Master'));
  const canMutateStatus = Boolean(isOwner || isAdmin);
  const [localTitle, setLocalTitle] = useState(action.title);
  const [localDesc, setLocalDesc] = useState(action.description || '');
  const [localPlan, setLocalPlan] = useState(action.actionPlan?.como || '');
  const [checklistInput, setChecklistInput] = useState('');
  const [commentInput, setCommentInput] = useState('');
  const [showUserPopover, setShowUserPopover] = useState(false);
  const [showImpactsEditor, setShowImpactsEditor] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Calcular tamanho de fonte baseado no zoom (85% a 115%)
  const fontSizePercent = Math.round(85 + (zoom - 0.5) * 30);
  
  // Buscar post-it vinculado e seus impactos
  const postIts = useProjectStore(state => state.postIts);
  const updatePostIt = useProjectStore(state => state.updatePostIt);
  const sheets = useProjectStore(state => state.sheets);
  const activeSheetId = useProjectStore(state => state.activeSheetId);
  
  const linkedPostIt = useMemo(() => {
    if (!action.linkedPostItId) return null;
    return postIts.find(p => p.id === action.linkedPostItId);
  }, [action.linkedPostItId, postIts]);
  
  const [localImpactos, setLocalImpactos] = useState<ActionImpact[]>([]);
  const [localInvestment, setLocalInvestment] = useState<number>(0);
  
  const activeCards = useMemo(() => {
    if (!activeSheetId || sheets.length === 0) return [];
    const activeSheet = sheets.find(s => s.id === activeSheetId);
    return activeSheet?.cards || [];
  }, [sheets, activeSheetId]);

  useEffect(() => {
    setLocalTitle(action.title);
    setLocalDesc(action.description || '');
    setLocalPlan(action.actionPlan?.como || '');
    setLocalImpactos(linkedPostIt?.impactos || []);
    setLocalInvestment(linkedPostIt?.investment || 0);
  }, [action.id, linkedPostIt]);

  const handleSmartUpdate = (updates: Partial<Action>) => {
    const currentPlan = action.actionPlan || {};
    const newPlan = { ...currentPlan };
    if (updates.responsavel !== undefined) newPlan.quem = updates.responsavel;
    if (updates.prazo !== undefined) newPlan.quando = updates.prazo;
    if (updates.status !== undefined) newPlan.status = updates.status;
    if ((updates as any).comments) newPlan.comments = (updates as any).comments;
    if ((updates as any).attachments) newPlan.attachments = (updates as any).attachments;
    if ((updates as any).checklist) newPlan.checklist = (updates as any).checklist;
    if ((updates as any).roiEstimado !== undefined) newPlan.quanto = (updates as any).roiEstimado;
    if ((updates as any).actionPlan) Object.assign(newPlan, (updates as any).actionPlan);
    
    // 📅 Gerenciar histórico de datas postergadas
    if (updates.prazo !== undefined && updates.prazo !== action.prazo) {
      const now = new Date().toISOString();
      const createdAt = action.createdAt || now;
      const creationDate = createdAt.split('T')[0];
      const today = new Date().toISOString().split('T')[0];
      
      // Se não for o mesmo dia da criação, adicionar ao histórico
      if (creationDate !== today && action.prazo) {
        const currentHistory = action.deadlineHistory || [];
        
        // Adicionar data antiga ao histórico apenas se ainda não houver histórico
        if (currentHistory.length === 0) {
          updates.deadlineHistory = [{
            oldDeadline: action.prazo,
            newDeadline: updates.prazo,
            changedAt: now,
            changedBy: user?.email || 'unknown',
            reason: 'Data postergada'
          }];
          updates.originalDeadline = action.prazo; // Guardar data original
        } else {
          // Já existe histórico, apenas atualizar
          updates.deadlineHistory = currentHistory;
        }
      }
    }
    
    onUpdateAction(action.id, { ...updates, actionPlan: newPlan as any });
  };

  const checklist = action.checklist ?? action.actionPlan?.checklist ?? [];
  const comments = action.comments ?? action.actionPlan?.comments ?? [];
  const attachments = action.attachments ?? action.actionPlan?.attachments ?? [];

  const handleChecklistAdd = () => { if (!checklistInput.trim()) return; const newList = [...checklist, { id: Date.now().toString(), text: checklistInput.trim(), checked: false }]; handleSmartUpdate({ checklist: newList as any }); setChecklistInput(''); };
  const toggleCheckitem = (id: string) => { const newList = checklist.map((i: any) => i.id === id ? { ...i, checked: !i.checked } : i); handleSmartUpdate({ checklist: newList as any }); };
  const removeCheckitem = (id: string) => { const newList = checklist.filter((i: any) => i.id !== id); handleSmartUpdate({ checklist: newList as any }); };
  const handleAddComment = () => { if (!commentInput.trim()) return; const authorName = user?.name || user?.email || 'Eu'; const newComment = { id: Date.now().toString(), text: commentInput.trim(), author: authorName, createdAt: new Date().toISOString() }; handleSmartUpdate({ comments: [...comments, newComment] as any }); setCommentInput(''); };
  const removeComment = (id: string) => { handleSmartUpdate({ comments: comments.filter((item: { id: string }) => item.id !== id) as any }); };
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => { const files = e.target.files; if (files?.[0]) handleSmartUpdate({ attachments: [...attachments, { id: Date.now().toString(), name: files[0].name, size: (files[0].size / 1024).toFixed(1) + ' KB', type: files[0].type, createdAt: new Date().toISOString() }] as any }); e.target.value = ''; };
  
  const handleAddImpacto = () => {
    const newImpacto: ActionImpact = { targetNodeId: '', tipo: 'TRA', reducao: 0 };
    setLocalImpactos([...localImpactos, newImpacto]);
  };
  
  const handleUpdateImpacto = (index: number, field: keyof ActionImpact, value: any) => {
    const updated = [...localImpactos];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'tipo' && value === 'ELIMINAR') {
      updated[index].reducao = 100;
    }
    setLocalImpactos(updated);
  };
  
  const handleRemoveImpacto = (index: number) => {
    setLocalImpactos(localImpactos.filter((_, i) => i !== index));
  };
  
  const handleSaveImpactos = () => {
    if (linkedPostIt && action.linkedPostItId) {
      updatePostIt(action.linkedPostItId, {
        impactos: localImpactos,
        investment: localInvestment
      });
      setShowImpactsEditor(false);
    }
  };

  const getHeaderTheme = () => {
    if (action.status === 'done') return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'Concluída', icon: CheckCircle2, tag: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
    if (action.status === 'doing') return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'Em Andamento', icon: Clock, tag: 'bg-blue-100 text-blue-800 border-blue-200' };
    if (action.prazo && new Date(action.prazo) < new Date()) return { bg: 'bg-red-50', border: 'border-red-200', text: 'Atrasada', icon: AlertCircle, tag: 'bg-red-100 text-red-800 border-red-200' };
    return { bg: 'bg-[var(--bg-canvas)]', border: 'border-[var(--border-default)]', text: 'A Fazer', icon: Circle, tag: 'bg-[var(--bg-elevated)] text-[var(--text-secondary)] border-[var(--border-default)]' };
  };
  const theme = getHeaderTheme();
  const progress = checklist.length > 0 ? Math.round((checklist.filter((i:any) => i.checked).length / checklist.length) * 100) : 0;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[var(--bg-canvas)] font-sans text-[var(--text-primary)]" style={{ fontSize: `${fontSizePercent}%` }}>
      
      {/* HEADER ULTRA-COMPACTO (SLIM) */}
      <div className={`flex h-12 shrink-0 items-center gap-3 border-b px-3 bg-[var(--bg-canvas)] ${theme.border}`}>
        
        {/* Botão Voltar + ID */}
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1 text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]">
            <ArrowLeft size={16} />
          </button>
          <div className={`flex items-center gap-1.5 border !border-[var(--border-default)] !bg-[var(--bg-elevated)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${theme.tag}`}>
            <theme.icon size={10} strokeWidth={3} />
            {theme.text}
          </div>
          <span className="text-[10px] font-mono text-[var(--text-tertiary)]">#{action.id.slice(-4)}</span>
        </div>

        {/* Separador */}
        <div className="h-4 w-px bg-[var(--border-default)]" />

        {/* Título (Centralizado Verticalmente) */}
          <input 
          className="h-8 min-w-0 flex-1 border border-transparent bg-transparent px-2 text-base font-semibold text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:border-[var(--border-subtle)] focus:bg-[var(--bg-surface)] focus:outline-none"
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          onBlur={() => handleSmartUpdate({ title: localTitle })}
          placeholder="Nome da Ação"
        />

        {/* Metadados (Alinhados à Direita) */}
        <div className="flex items-center gap-3">
          
          <div className="relative">
            <button onClick={() => setShowUserPopover(!showUserPopover)} className="flex items-center gap-2 border border-transparent px-2 py-1 hover:border-[var(--border-default)] hover:bg-[var(--bg-elevated)]">
              <div className={`flex h-5 w-5 items-center justify-center text-[9px] font-bold ${action.responsavel ? 'bg-[var(--bg-surface-3)] text-[var(--text-primary)]' : 'bg-[var(--bg-elevated)] text-[var(--text-tertiary)]'}`}>
                {action.responsavel ? action.responsavel.charAt(0).toUpperCase() : <User size={10}/>}
              </div>
              <span className={`text-xs font-medium ${action.responsavel ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`}>{action.responsavel || "Atribuir"}</span>
            </button>
            {showUserPopover && <><div className="fixed inset-0 z-30" onClick={() => setShowUserPopover(false)} /><UserAssignmentPopover currentValue={action.responsavel} onClose={() => setShowUserPopover(false)} onSelect={(name) => handleSmartUpdate({ responsavel: name })} /></>}
          </div>

          <div className="group relative flex cursor-pointer flex-col gap-1 border border-transparent px-2 py-1 hover:border-[var(--border-default)] hover:bg-[var(--bg-elevated)]">
            <div className="flex items-center gap-2">
              <Calendar size={14} className={action.prazo ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'} />
              <input 
                type="date" 
                className="w-auto cursor-pointer border-none bg-transparent p-0 text-xs font-medium text-[var(--text-secondary)] focus:outline-none" 
                value={action.prazo ? new Date(action.prazo + 'T00:00:00').toISOString().split('T')[0] : ''} 
                onChange={(e) => handleSmartUpdate({ prazo: e.target.value })} 
              />
            </div>
            {/* Exibir data postergada (tachada) se houver histórico */}
            {action.deadlineHistory && action.deadlineHistory.length > 0 && (
              <div className="flex items-center gap-1 text-[10px] ml-5">
                <span className="line-through text-red-500 font-medium">
                  {formatDateBR(action.deadlineHistory[0].oldDeadline)}
                </span>
                <span className="text-[var(--text-tertiary)]">→</span>
                <span className="text-[var(--text-secondary)] font-medium">
                  {formatDateBR(action.prazo)}
                </span>
              </div>
            )}
          </div>

        <div className="h-4 w-px bg-[var(--border-default)]" />

          <button 
            onClick={() => canMutateStatus && handleSmartUpdate({ status: action.status === 'done' ? 'todo' : 'done' })}
            disabled={!canMutateStatus}
            className={`flex items-center gap-1.5 border px-2.5 py-1 text-[10px] font-semibold ${
              action.status === 'done' 
                ? 'bg-[var(--bg-surface)] text-emerald-600 border-[var(--border-default)] hover:bg-[var(--bg-elevated)]' 
                : 'bg-[var(--bg-elevated)] text-[var(--text-primary)] border-[var(--border-default)] hover:bg-[var(--bg-surface-2)]'
            } ${!canMutateStatus ? 'pointer-events-none opacity-90' : ''}`}
          >
            <CheckCircle2 size={12} className={action.status === 'done' ? 'fill-emerald-100' : ''} />
            {action.status === 'done' ? 'Reabrir' : 'Concluir'}
          </button>
        </div>
      </div>

      {/* --- BODY (COMPACTO) --- */}
      <div className="flex-1 overflow-hidden p-3">
        <div className="mx-auto grid h-full max-w-[1920px] grid-cols-12 gap-3">
          
          {/* COLUNA ESQUERDA: Definições (25%) */}
           <div className="col-span-3 flex h-full flex-col gap-3 overflow-y-auto pr-1 custom-scrollbar">
            
            <div className="flex flex-col border border-[var(--border-default)] bg-[var(--bg-surface)]">
              <div className="border-b border-[var(--border-default)] px-3 py-2">
                <h3 className="text-[10px] font-bold text-[var(--text-tertiary)] uppercase flex items-center gap-1.5"><Layout size={12} /> Contexto</h3>
              </div>
              <div className="space-y-2.5 p-2.5">
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-[var(--text-secondary)] uppercase tracking-wide">O Problema</label>
                  {/* REDUZIDO HEIGHT */}
                  <textarea 
                    className="h-16 w-full resize-none border border-[var(--border-default)] bg-[var(--bg-canvas)] p-2 text-xs leading-snug text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:border-[var(--nav-active-bar)] focus:outline-none"
                    placeholder="Descreva..."
                    value={localDesc}
                    onChange={(e) => setLocalDesc(e.target.value)}
                    onBlur={() => handleSmartUpdate({ description: localDesc })}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-[var(--text-secondary)] uppercase tracking-wide">O Plano</label>
                  {/* REDUZIDO HEIGHT */}
                  <textarea 
                    className="h-20 w-full resize-none border border-[var(--border-default)] bg-[var(--bg-canvas)] p-2 text-xs leading-snug text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:border-[var(--nav-active-bar)] focus:outline-none"
                    placeholder="Execução..."
                    value={localPlan}
                    onChange={(e) => setLocalPlan(e.target.value)}
                    onBlur={() => handleSmartUpdate({ actionPlan: { ...action.actionPlan, como: localPlan } as any })}
                  />
                </div>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col border border-[var(--border-default)] bg-[var(--bg-surface)]">
              <div className="flex items-center justify-between border-b border-[var(--border-default)] px-3 py-2">
                <h3 className="text-[10px] font-bold text-[var(--text-tertiary)] uppercase flex items-center gap-1.5"><ListChecks size={12} /> Checklist</h3>
                <span className="border border-[var(--border-default)] bg-[var(--bg-elevated)] px-1.5 text-[9px] font-bold text-[var(--text-secondary)]">{checklist.filter((i:any)=>i.checked).length}/{checklist.length}</span>
              </div>
              
              <div className="h-0.5 w-full bg-[var(--bg-elevated)]">
                <div className="h-full bg-[var(--nav-active-bar)]" style={{ width: `${progress}%` }} />
              </div>

              <div className="flex min-h-0 flex-1 flex-col p-2.5">
                <div className="mb-2 flex-1 space-y-0.5 overflow-y-auto custom-scrollbar">
                  {checklist.map((item: any) => (
                    <div key={item.id} className="group flex cursor-pointer items-start gap-2 p-1 hover:bg-[var(--bg-elevated)]" onClick={() => toggleCheckitem(item.id)}>
                      <div className={`mt-0.5 flex h-3 w-3 flex-shrink-0 items-center justify-center border ${item.checked ? 'border-[var(--nav-active-bar)] bg-[var(--nav-active-bar)] text-white' : 'border-[var(--border-subtle)] bg-[var(--bg-canvas)]'}`}>{item.checked && <CheckCircle2 size={8} />}</div>
                      <span className={`flex-1 text-xs leading-tight ${item.checked ? 'text-[var(--text-tertiary)] line-through' : 'text-[var(--text-primary)]'}`}>{item.text}</span>
                       <button onClick={(e) => {e.stopPropagation(); removeCheckitem(item.id)}} className="text-[var(--text-tertiary)] opacity-0 group-hover:opacity-100 hover:text-rose-500"><Trash2 size={12} /></button>
                    </div>
                  ))}
                  {checklist.length === 0 && <p className="px-1 py-3 text-center text-[12px] leading-relaxed text-[var(--text-tertiary)]">Nenhum item. Escreva abaixo e pressione Enter.</p>}
                </div>
                <div className="flex min-w-0 gap-1">
                  <input className="min-w-0 flex-1 border border-[var(--border-default)] bg-[var(--bg-canvas)] px-2 py-1.5 text-[13px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none focus:border-[var(--nav-active-bar)]" placeholder="Novo item" value={checklistInput} onChange={(e) => setChecklistInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleChecklistAdd(); } }} />
                  <button type="button" onClick={handleChecklistAdd} title="Adicionar item" className="shrink-0 border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2.5 text-[13px] font-bold text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)]">+</button>
                </div>
              </div>
            </div>
          </div>

          {/* COLUNA CENTRAL: Histórico (50%) */}
          <div className="col-span-6 flex h-full flex-col overflow-hidden border border-[var(--border-default)] bg-[var(--bg-surface)]">
            <div className="flex items-center justify-between border-b border-[var(--border-default)] px-3 py-2">
              <h3 className="text-[10px] font-bold text-[var(--text-tertiary)] uppercase flex items-center gap-1.5"><Activity size={12} /> Comentários</h3>
            </div>
            
            <div className="flex-1 space-y-3 overflow-y-auto bg-[var(--bg-surface)] p-3 custom-scrollbar">
              {comments.length === 0 && (
                <div className="flex h-full flex-col items-center justify-center px-6 text-center text-[13px] text-[var(--text-tertiary)]">
                  <MessageSquare size={22} className="mb-2" />
                  Nenhum comentário. Escreva abaixo para registrar o andamento.
                </div>
              )}
              {comments.map((comment: any) => (
                <div key={comment.id} className="flex gap-2 group">
                  <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center border border-[var(--border-default)] bg-[var(--bg-elevated)] text-[9px] font-bold text-[var(--text-secondary)]">
                    {comment.author ? comment.author.charAt(0) : 'U'}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <span className="text-[11px] font-bold text-[var(--text-secondary)]">{comment.author}</span>
                      <span className="text-[9px] text-[var(--text-tertiary)]">{new Date(comment.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="inline-block max-w-[95%] border border-[var(--border-default)] bg-[var(--bg-canvas)] p-2 text-xs text-[var(--text-primary)]">
                      {comment.text}
                    </div>
                    <button type="button" onClick={() => removeComment(comment.id)} className="mt-1 text-[10px] text-[var(--text-tertiary)] opacity-0 hover:text-rose-500 group-hover:opacity-100">Remover</button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[var(--border-default)] bg-[var(--bg-surface)] p-2.5">
              <div className="relative border border-[var(--border-default)] bg-[var(--bg-canvas)] focus-within:border-[var(--nav-active-bar)]">
                <textarea 
                  className="h-11 w-full resize-none border-none bg-transparent py-2 pl-3 pr-8 text-xs text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none"
                  placeholder="Escreva um comentário..."
                  value={commentInput}
                  onChange={(e) => setCommentInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); }}}
                />
                <button type="button" onClick={handleAddComment} className="absolute bottom-1.5 right-1.5 bg-[var(--bg-surface-3)] p-1 text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)] disabled:opacity-50" disabled={!commentInput.trim()}>
                  <Send size={10} />
                </button>
              </div>
            </div>
          </div>

          {/* COLUNA DIREITA: KPIs (25%) */}
          <div className="col-span-3 flex h-full flex-col gap-3 overflow-y-auto pr-1 custom-scrollbar">
            
            <div className="border border-[var(--border-default)] bg-[var(--bg-surface)] p-2.5">
              <label className="text-[9px] font-bold text-[var(--text-secondary)] uppercase mb-1.5 block tracking-wide">Status</label>
              <select 
                className={`mb-2 w-full cursor-pointer border px-2 py-1.5 text-xs font-semibold outline-none ${action.status === 'done' ? 'border-[var(--border-default)] bg-[var(--bg-elevated)] text-emerald-600' : 'border-[var(--border-default)] bg-[var(--bg-canvas)] text-[var(--text-primary)]'} ${!canMutateStatus ? 'pointer-events-none opacity-90' : ''}`}
                value={action.status}
                onChange={(e) => canMutateStatus && handleSmartUpdate({ status: e.target.value as any })}
                disabled={!canMutateStatus}
              >
                <option value="todo">A Fazer</option>
                <option value="doing">Em Andamento</option>
                <option value="done">Concluída</option>
                <option value="canceled">Cancelada</option>
              </select>
                  {(() => {
                    const isOverdue = !!action.prazo && new Date(action.prazo) < new Date();
                    const daysLate = isOverdue ? Math.max(1, Math.ceil((Date.now() - new Date(action.prazo).getTime()) / 86400000)) : 0;
                    if (isOverdue && (action.status === 'doing' || action.status === 'todo')) {
                      return (
                        <div className="bg-red-50 text-red-600 border border-red-200/60 rounded-lg p-2 text-xs font-medium">
                          {`⚠️ Atrasada há ${daysLate} ${daysLate === 1 ? 'dia' : 'dias'}`}
                        </div>
                      );
                    }
                    return (
                      <div className="flex items-center gap-1.5 border border-[var(--border-default)] bg-[var(--bg-canvas)] p-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${isOverdue ? 'bg-red-500' : 'bg-emerald-500'}`} />
                        <span className="text-[9px] font-medium text-[var(--text-tertiary)] truncate">
                          {!action.prazo ? 'Sem prazo' : isOverdue ? 'Atrasada' : 'No prazo'}
                        </span>
                      </div>
                    );
                  })()}
            </div>

            {/* Ganhos Projetados (Impactos) */}
            {linkedPostIt && (
              <div className="border border-[var(--border-default)] bg-[var(--bg-surface)] p-2.5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-[var(--text-secondary)]">
                    <Target size={12} /> Ganhos Projetados
                  </h3>
                  <button
                    onClick={() => setShowImpactsEditor(!showImpactsEditor)}
                    className="border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-1 text-[9px] font-bold text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)]"
                  >
                    {showImpactsEditor ? 'Fechar' : 'Editar'}
                  </button>
                </div>
                
                {!showImpactsEditor ? (
                  <div className="space-y-1.5">
                    {localImpactos.length === 0 ? (
                      <p className="text-[10px] italic text-[var(--text-tertiary)]">Nenhum impacto definido</p>
                    ) : (
                      localImpactos.map((imp, idx) => {
                        const card = activeCards.find(c => c.id === imp.targetNodeId);
                        return (
                          <div key={idx} className="border border-[var(--border-default)] bg-[var(--bg-canvas)] px-2 py-1.5">
                            <div className="flex items-center gap-1.5">
                              <TrendingDown size={10} className="text-[var(--text-secondary)]" />
                              <span className="text-[10px] font-semibold text-[var(--text-secondary)] flex-1 truncate">
                                {card?.atividade || 'Card não encontrado'}
                              </span>
                            </div>
                            <div className="text-[9px] text-[var(--text-tertiary)] mt-0.5">
                              {imp.tipo === 'TRA' && '⏱️ Trabalho'}
                              {imp.tipo === 'TP' && '⏸️ Espera'}
                              {imp.tipo === 'ELIMINAR' && '❌ Eliminar'}
                              {' • '}
                              <span className="font-bold text-[var(--text-secondary)]">{imp.reducao}%</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                    {localInvestment > 0 && (
                      <div className="mt-2 border-t border-[var(--border-default)] pt-2">
                        <div className="text-[9px] font-semibold text-[var(--text-secondary)]">Investimento</div>
                        <div className="text-xs font-bold text-[var(--text-primary)]">R$ {localInvestment.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {localImpactos.map((impacto, index) => (
                      <div key={index} className="space-y-1.5 border border-[var(--border-default)] bg-[var(--bg-canvas)] p-2">
                        <select
                          value={impacto.targetNodeId}
                          onChange={(e) => handleUpdateImpacto(index, 'targetNodeId', e.target.value)}
                          className="w-full border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 py-1 text-[10px] text-[var(--text-primary)] focus:border-[var(--nav-active-bar)] focus:outline-none"
                        >
                          <option value="">Selecione o processo...</option>
                          {activeCards.filter(c => c.tipo === 'atividade' || c.tipo === 'decisao').map(card => (
                            <option key={card.id} value={card.id}>
                              {card.atividade || card.decisao || `Card ${card.id}`}
                            </option>
                          ))}
                        </select>
                        <div className="grid grid-cols-2 gap-1.5">
                          <select
                            value={impacto.tipo}
                            onChange={(e) => handleUpdateImpacto(index, 'tipo', e.target.value)}
                            className="w-full border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 py-1 text-[10px] text-[var(--text-primary)] focus:border-[var(--nav-active-bar)] focus:outline-none"
                          >
                            <option value="TRA">⏱️ Trabalho</option>
                            <option value="TP">⏸️ Espera</option>
                            <option value="ELIMINAR">❌ Eliminar</option>
                          </select>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={impacto.reducao}
                            onChange={(e) => handleUpdateImpacto(index, 'reducao', Number(e.target.value))}
                            disabled={impacto.tipo === 'ELIMINAR'}
                            className="w-full border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 py-1 text-[10px] text-[var(--text-primary)] focus:border-[var(--nav-active-bar)] focus:outline-none disabled:bg-[var(--bg-elevated)]"
                            placeholder="%"
                          />
                        </div>
                        <button
                          onClick={() => handleRemoveImpacto(index)}
                          className="w-full py-1 text-[9px] font-medium text-rose-500 hover:bg-[var(--bg-elevated)]"
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={handleAddImpacto}
                      className="flex w-full items-center justify-center gap-1 border border-dashed border-[var(--border-subtle)] py-1.5 text-[10px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]"
                    >
                      <Plus size={12} /> Adicionar Impacto
                    </button>
                    <div className="border-t border-[var(--border-default)] pt-2">
                      <label className="mb-1 block text-[9px] font-bold text-[var(--text-secondary)]">Investimento (R$)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={localInvestment}
                        onChange={(e) => setLocalInvestment(Number(e.target.value))}
                        className="w-full border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 py-1.5 text-xs text-[var(--text-primary)] focus:border-[var(--nav-active-bar)] focus:outline-none"
                        placeholder="0.00"
                      />
                    </div>
                    <button
                      onClick={handleSaveImpactos}
                      className="mt-2 w-full border border-[var(--border-default)] bg-[var(--bg-elevated)] py-1.5 text-[10px] font-bold text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)]"
                    >
                      Salvar Ganhos
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="border border-[var(--border-default)] bg-[var(--bg-surface)] p-2.5">
              <h3 className="text-[10px] font-bold text-[var(--text-tertiary)] uppercase flex items-center gap-1.5 mb-2"><DollarSign size={12} /> Resultados</h3>
              <div className="space-y-2">
                <div>
                  <label className="text-[9px] font-bold text-[var(--text-secondary)] mb-1 block">CUSTO REAL</label>
                  <div className="group flex items-center gap-1.5 border border-[var(--border-default)] bg-[var(--bg-canvas)] px-2 py-1.5 focus-within:border-[var(--nav-active-bar)]">
                    <span className="text-[10px] font-bold text-[var(--text-tertiary)] group-focus-within:text-[var(--text-secondary)]">R$</span>
                    <input 
                      type="number" 
                      className="w-full border-none bg-transparent p-0 text-xs font-bold text-[var(--text-primary)] focus:outline-none"
                      placeholder="0,00"
                      value={action.actionPlan?.quanto || ''}
                      onChange={(e) => handleSmartUpdate({ actionPlan: { ...action.actionPlan, quanto: parseFloat(e.target.value) } as any })}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col border border-[var(--border-default)] bg-[var(--bg-surface)] p-2.5">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-[10px] font-bold text-[var(--text-tertiary)] uppercase flex items-center gap-1.5"><Paperclip size={12} /> Arquivos</h3>
                <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} />
                <button onClick={() => fileInputRef.current?.click()} className="border border-[var(--border-default)] bg-[var(--bg-elevated)] px-1.5 py-0.5 text-[9px] font-bold text-[var(--text-primary)] hover:bg-[var(--bg-surface-2)]">+ Add</button>
              </div>
              
              <div className="space-y-1.5 flex-1 overflow-y-auto min-h-0 custom-scrollbar">
                {attachments.map((file: any) => (
                  <div key={file.id} className="group flex items-center gap-2 border border-[var(--border-default)] bg-[var(--bg-canvas)] p-1.5 hover:bg-[var(--bg-elevated)]">
                    <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center border border-[var(--border-default)] bg-[var(--bg-surface)] text-[var(--text-secondary)]"><FileText size={10} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-medium text-[var(--text-secondary)] truncate">{file.name}</p>
                      <p className="text-[8px] text-[var(--text-tertiary)]">{file.size}</p>
                    </div>
                  </div>
                ))}
                
                {attachments.length === 0 && (
                  <div onClick={() => fileInputRef.current?.click()} className="flex h-16 cursor-pointer flex-col items-center justify-center border border-dashed border-[var(--border-subtle)] text-[var(--text-tertiary)] hover:bg-[var(--bg-elevated)] hover:border-[var(--nav-active-bar)]">
                    <Upload size={14} className="mb-1 opacity-50" />
                    <span className="text-[9px] font-medium">Anexar</span>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ActionDetailView;
